import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { UserTable } from "@/components/admin/users/UserTable";
import { UserProfile } from "@/components/admin/users/UserRow";
import { DeleteUserDialog } from "@/components/admin/users/DeleteUserDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useDebounce } from "@/hooks/useDebounce";
import { useUserDataExtended } from "@/hooks/users/useUserDataExtended"; 
import { useUserDelete } from "@/hooks/users/useUserDelete";
import { Search, RefreshCw, MessageSquare } from "lucide-react";
import { SmsInviteDialog } from "@/components/admin/users/SmsInviteDialog";
import { InvitationsTable } from "@/components/admin/users/InvitationsTable";

const ManageUsers = () => {
  const {
    users,
    isLoading: dataLoading,
    refetchWithCompleteness,
    updateUserRoleMutation,
    updateProfileMutation
  } = useUserDataExtended();
  
  const { deleteUserMutation, isDeleting } = useUserDelete();
  
  const [activeTab, setActiveTab] = useState<"all" | "admin" | "vendor" | "user" | "invitations">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<"email" | "display_name" | "role">("email");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserProfile | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSmsInviteOpen, setIsSmsInviteOpen] = useState(false);
  const [invitationsRefreshTrigger, setInvitationsRefreshTrigger] = useState(0);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Memoize the filtering and sorting operations together to reduce re-renders
  const processedUsers = useMemo(() => {
    if (!users) return [];

    let result = [...users];

    // Apply tab filter
    if (activeTab !== "all") {
      result = result.filter((user) => user.role === activeTab);
    }

    // Apply search filter
    if (debouncedSearchQuery) {
      const searchStr = debouncedSearchQuery.toLowerCase();
      result = result.filter((user) => {
        return (
          (user.email && user.email.toLowerCase().includes(searchStr)) ||
          (user.display_name && user.display_name.toLowerCase().includes(searchStr))
        );
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      const direction = sortDirection === "asc" ? 1 : -1;
      switch (sortField) {
        case "email":
          return (a.email || "").localeCompare(b.email || "") * direction;
        case "display_name":
          return (a.display_name || "").localeCompare(b.display_name || "") * direction;
        case "role":
          return a.role.localeCompare(b.role) * direction;
        default:
          return 0;
      }
    });

    return result;
  }, [users, activeTab, debouncedSearchQuery, sortField, sortDirection]);

  const handleTabChange = useCallback((tab: "all" | "admin" | "vendor" | "user" | "invitations") => {
    setActiveTab(tab);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleSort = useCallback((field: "email" | "display_name" | "role") => {
    setSortField(prev => {
      if (prev === field) {
        setSortDirection(prevDir => prevDir === "asc" ? "desc" : "asc");
        return prev;
      } else {
        setSortDirection("asc");
        return field;
      }
    });
  }, []);

  const handleEditUser = useCallback((user: UserProfile) => {
    navigate(`/admin/users/edit/${user.id}`);
  }, [navigate]);

  const handleDeleteUser = useCallback((user: UserProfile) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refetchWithCompleteness();
      toast({
        title: "Users refreshed",
        description: "User data has been updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Refresh failed",
        description: "Failed to refresh user data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [refetchWithCompleteness, toast]);

  const confirmDelete = useCallback(async () => {
    if (!userToDelete) return;
    
    try {
      await deleteUserMutation.mutateAsync(userToDelete.id);
      refetchWithCompleteness();
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  }, [userToDelete, deleteUserMutation, refetchWithCompleteness]);

  return (
    <AdminLayout title="Manage Users">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList>
              <TabsTrigger value="all">All Users</TabsTrigger>
              <TabsTrigger value="admin">Admins</TabsTrigger>
              <TabsTrigger value="vendor">Vendors</TabsTrigger>
              <TabsTrigger value="user">Users</TabsTrigger>
              <TabsTrigger value="invitations">Invitations</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex gap-2">
            {(activeTab === "vendor" || activeTab === "invitations") && (
              <Button 
                onClick={() => setIsSmsInviteOpen(true)} 
                variant="outline"
                size="sm"
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                SMS Invite
              </Button>
            )}
            {activeTab !== "invitations" && (
              <Button 
                onClick={handleRefresh} 
                disabled={isRefreshing || dataLoading}
                variant="outline"
                size="sm"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            )}
          </div>
        </div>
        {activeTab !== "invitations" && (
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              id="search"
              placeholder="Search by email or display name..."
              className="pl-10"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsContent value="all">
          <UserTable
            users={processedUsers}
            isLoading={dataLoading}
            sortField={sortField}
            sortDirection={sortDirection}
            handleSort={handleSort}
            onEditUser={handleEditUser}
            onDeleteUser={handleDeleteUser}
            searchQuery={searchQuery}
            activeTab={activeTab}
          />
        </TabsContent>
        <TabsContent value="admin">
          <UserTable
            users={processedUsers}
            isLoading={dataLoading}
            sortField={sortField}
            sortDirection={sortDirection}
            handleSort={handleSort}
            onEditUser={handleEditUser}
            onDeleteUser={handleDeleteUser}
            searchQuery={searchQuery}
            activeTab={activeTab}
          />
        </TabsContent>
        <TabsContent value="vendor">
          <UserTable
            users={processedUsers}
            isLoading={dataLoading}
            sortField={sortField}
            sortDirection={sortDirection}
            handleSort={handleSort}
            onEditUser={handleEditUser}
            onDeleteUser={handleDeleteUser}
            searchQuery={searchQuery}
            activeTab={activeTab}
          />
        </TabsContent>
        <TabsContent value="user">
          <UserTable
            users={processedUsers}
            isLoading={dataLoading}
            sortField={sortField}
            sortDirection={sortDirection}
            handleSort={handleSort}
            onEditUser={handleEditUser}
            onDeleteUser={handleDeleteUser}
            searchQuery={searchQuery}
            activeTab={activeTab}
          />
        </TabsContent>
        <TabsContent value="invitations">
        <InvitationsTable 
          refreshTrigger={invitationsRefreshTrigger}
          onInvitationsChange={() => setInvitationsRefreshTrigger(prev => prev + 1)}
        />
        </TabsContent>
      </Tabs>

      <DeleteUserDialog
        isOpen={isDeleteDialogOpen}
        setIsOpen={setIsDeleteDialogOpen}
        userToDelete={userToDelete}
        isDeleting={isDeleting}
        confirmDelete={confirmDelete}
      />

      <SmsInviteDialog
        isOpen={isSmsInviteOpen}
        setIsOpen={setIsSmsInviteOpen}
        onInviteSent={() => setInvitationsRefreshTrigger(prev => prev + 1)}
      />
    </AdminLayout>
  );
};

export default ManageUsers;
